/*
 * Shimmer.tsx
 * Shimmer effect component that adds a light sweep animation across an element.
 */
'use client';

import React, { forwardRef, useRef, useImperativeHandle } from 'react';
import { gsap } from 'gsap';

// Styles
import styles from './Shimmer.module.scss';
import classNames from 'classnames/bind';
const cx = classNames.bind(styles);

// --------------------

export interface ShimmerRef {
  shimmer: () => void;
}

interface ShimmerProps {
  children: React.ReactNode;
  className?: string;
}

// -------------------- Component

const Shimmer = forwardRef<ShimmerRef, ShimmerProps>(({
  children,
  className
}, ref) => {
  const shimmerElementRef = useRef<HTMLDivElement>(null);
  const shimmerOverlayRef = useRef<HTMLDivElement>(null);

  // Shimmer animation function
  const playShimmer = () => {
    if (!shimmerOverlayRef.current) return;

    // Create timeline for shimmer animation
    const tl = gsap.timeline();

    // Show overlay, animate from left to right, then hide
    tl.set(shimmerOverlayRef.current, { autoAlpha: 1 })
      .fromTo(shimmerOverlayRef.current, {
        x: '-80%'
      }, {
        x: '80%',
        duration: 0.8,
        ease: 'power1.inOut'
      })
      .set(shimmerOverlayRef.current, { autoAlpha: 0 });
  };

  // Expose shimmer function to parent
  useImperativeHandle(ref, () => ({
    shimmer: playShimmer
  }));

  return (
    <div className={cx('shimmer', className)}
      ref={shimmerElementRef}
      onClick={playShimmer}
    >
      {children}
      {/* Shimmer overlay element */}
      <div ref={shimmerOverlayRef} className={cx('shimmer-overlay')} />
    </div>
  );
});

Shimmer.displayName = 'Shimmer';

export default Shimmer;
